import ProjectNavigationSidebar from '@app/shared/layout/project-navigation-sidebar/ProjectNavigationSidebar';
import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styles from './project-routes-wrapper.module.scss';
import { useAppSelector } from '@core/hooks/redux-hooks';
import { ContentHeaderLayout } from '@app/modules/projects/project-routes-wrapper/layout-enums';
import classNames from 'classnames';
import { setPageScrollPosition } from '@core/store/ui-values-slice';
import { useDispatch } from 'react-redux';
import { RouteConstants } from '../RouteConstants';
import { ContainerTestId } from '@app/shared/models/contracts/enums/shared-enums';
import { useStickyOffset } from '@app/core/hooks/useStickyOffset';

const PARENT_CLASSNAME = 'project-routes-wrapper';

type PageLayoutProps = {
  children: ReactNode;
  contentHeader?: ReactNode;
  contentHeaderLayout?: ContentHeaderLayout;
  rightSidebar?: ReactNode;
  pageName: string;
};

const PageLayout: FC<PageLayoutProps> = ({
  children,
  contentHeader,
  contentHeaderLayout,
  rightSidebar,
  pageName,
}): JSX.Element => {
  const [scrollTop, setScrollTop] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const mainContainer = useRef<HTMLElement>(null);
  const scrollTopRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const projectId = useAppSelector((state) => state.project.projectDraft.id);
  const pageScrollPositionList = useAppSelector(
    (state) => state.uiValues.pageScrollPositionList[projectId]
  );
  const dispatch = useDispatch();
  const { setTopElementHeight, useResizeListener } = useStickyOffset();

  const handleScroll = () => {
    setScrollTop(mainContainer.current?.scrollTop ?? 0);
    setScrollLeft(mainContainer.current?.scrollLeft ?? 0);
  };

  useEffect(() => {
    scrollTopRef.current = scrollTop;
    scrollLeftRef.current = scrollLeft;
  }, [scrollTop, scrollLeft]);

  const capStructureHeaderRef = useRef<HTMLDivElement>(null);

  const setCapitalStructureHeaderHeight = useCallback(() => {
    setTopElementHeight(capStructureHeaderRef, '--capital-structure-header-height');
  }, [setTopElementHeight]);

  useEffect(() => {
    setCapitalStructureHeaderHeight();
  }, [setCapitalStructureHeaderHeight]);

  useResizeListener(setCapitalStructureHeaderHeight, capStructureHeaderRef);

  useEffect(() => {
    if (pageScrollPositionList) {
      const pageScrollPosition = pageScrollPositionList.find((page) => page.pageName === pageName);

      if (mainContainer.current && pageScrollPosition) {
        const targetScrollTop = pageScrollPosition.scrollTop;

        const attemptScroll = () => {
          if (!mainContainer.current) {
            return;
          }

          const maxScrollable =
            mainContainer.current.scrollHeight - mainContainer.current.clientHeight;

          if (maxScrollable <= 0 && targetScrollTop > 0) {
            requestAnimationFrame(attemptScroll);
            return;
          }

          const scrollToPosition = Math.min(targetScrollTop, maxScrollable);

          mainContainer.current.scrollTo(pageScrollPosition.scrollLeft, scrollToPosition);
        };

        requestAnimationFrame(attemptScroll);
      }
    }

    return () => {
      if (pageName !== RouteConstants.PayoutLogic) {
        const actionPayload = {
          key: projectId,
          value: {
            pageName: pageName,
            scrollTop: scrollTopRef.current,
            scrollLeft: scrollLeftRef.current,
          },
        };
        dispatch(setPageScrollPosition(actionPayload));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contentSection = (
    <>
      {contentHeader && (
        <div
          className={classNames(styles[`${PARENT_CLASSNAME}__header-container`], {
            [styles[`${PARENT_CLASSNAME}__header-container--${contentHeaderLayout}`]]:
              contentHeaderLayout,
          })}
          ref={capStructureHeaderRef}>
          {contentHeader}
        </div>
      )}
      {children}
    </>
  );

  return (
    <>
      <main
        className={styles[`${PARENT_CLASSNAME}__main`]}
        data-testid={ContainerTestId.Main}
        ref={mainContainer}
        onScroll={handleScroll}>
        {contentSection}
      </main>
      {rightSidebar}
    </>
  );
};

type ProjectRoutesWrapperProps = {
  children: ReactNode;
};

const ProjectRoutesWrapper: FC<ProjectRoutesWrapperProps> & {
  PageLayout: FC<PageLayoutProps>;
  // eslint-disable-next-line react/prop-types
} = ({ children }): JSX.Element => {
  const { projectId } = useParams();
  const project = useAppSelector((state) => state.project);

  useEffect(() => {
    document.title = `${
      !project.projectDraft.name ? '' : project.projectDraft.name + ' - '
    }Equity Reward Analytics`;
  }, [project]);

  if (projectId === project?.projectDraft?.id?.toString()) {
    return (
      <div className={classNames(styles[PARENT_CLASSNAME])}>
        <ProjectNavigationSidebar
          collapsible
          collapsibleStorageKey="collapsed-sidebar"
          containerClassName={styles[`${PARENT_CLASSNAME}__sidebar-container`]}
          collapsedClassName={styles['collapsed']}
          containerTestId="sidebar-container"
        />
        {children}
      </div>
    );
  } else {
    return <></>;
  }
};

ProjectRoutesWrapper.PageLayout = PageLayout;

export default ProjectRoutesWrapper;
